<template>
  <ValidationObserver v-slot="{ passes }">
    <b-modal id="create-shipping-order-modal" size="xl" @show="onShow"
      ><template v-slot:modal-header="{ close }">
        <b-container>
          <div class="modal-title">
            <h3 class="text-bold text-center">Create Shipping Order</h3>
            <button class="close" type="button" aria-label="Close" @click="close">
              <i class="di-remove-10"></i>
            </button>
          </div>
        </b-container>
      </template>
      <div>
        <div class="container" v-if="step === 1">
          <h5 class="mb-4">Shipping Details</h5>
          <b-form ref="createShippingOrderForm">
            <section>
              <h6 class="text-uppercase mb-3">Sender</h6>
              <b-row>
                <b-col cols="6">
                  <b-form-group label="Pickup location" label-for="location">
                    <b-form-select v-model="form.sender.outlet" name="location" required="">
                      <b-form-select-option
                        v-for="outlet in outletsWithAddress"
                        :key="outlet.id"
                        :value="outlet"
                        >{{ getAddress(outlet.address) }}</b-form-select-option
                      >
                    </b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>
            </section>
          </b-form>
          <div class="divider mt-2 mb-4"></div>
          <section>
            <h6 class="text-uppercase mb-3">Receiver</h6>
            <div v-if="selectedAddress">
              <b-row class="mb-4">
                <b-col cols="4"
                  ><label>Recipient</label><br /><span
                    >{{ selectedAddress.first_name }} {{ selectedAddress.last_name }}</span
                  ></b-col
                >
                <b-col cols="4"
                  ><label>Email address</label><br /><span>{{ selectedAddress.email }}</span></b-col
                >
                <b-col cols="4"
                  ><label>Phone</label><br /><span>{{ selectedAddress.mobile }}</span></b-col
                >
              </b-row>
              <b-row class="mb-4">
                <b-col cols="4"
                  ><label>Street Address</label><br /><span>{{
                    selectedAddress.address.address1
                  }}</span
                  ><br /><span v-if="selectedAddress.address.address2">{{
                    selectedAddress.address.address2
                  }}</span></b-col
                >
                <b-col cols="4"
                  ><label>Suburb</label><br /><span
                    >{{ selectedAddress.address.suburb }}
                    {{ selectedAddress.address.country && selectedAddress.address.country.toUpperCase() !== 'AU' ? selectedAddress.address.city : '' }}
                    {{ selectedAddress.address.state }}
                    {{ selectedAddress.address.postcode }}</span
                  ></b-col
                >
                <b-col cols="4"
                  ><label>Country</label><br /><span>{{
                    selectedAddress.address.country
                  }}</span></b-col
                >
              </b-row>
            </div>
            <b-row>
              <b-col cols="12">
                <b-button
                  class="mr-3"
                  variant="outline-light"
                  v-if="selectedAddress"
                  style="width: 100px"
                  @click="editAddress"
                  >Edit</b-button
                >
                <b-button variant="primary" @click="showAddressModal">{{
                  selectedAddress ? 'Change Address' : 'Choose Address'
                }}</b-button>
              </b-col>
            </b-row>
          </section>
          <div class="divider mt-2 mb-4"></div>
          <section v-if="isAddressCa">
            <h6 class="text-uppercase mb-3">Order Tracking</h6>
            <b-row>
              <b-col cols="12">
                <ValidationProvider
                  vid="ca_order_tracking"
                  name="Order Tracking URL"
                  mode="lazy"
                  :rules="{
                    regex:
                      /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/,
                    required: true,
                  }"
                  v-slot="validationContext"
                >
                  <b-form-group label="Order Tracking URL" label-for="ca_order_tracking">
                    <b-form-input
                      :state="getValidationState(validationContext)"
                      id="ca_order_tracking"
                      v-model="form.ca_order_tracking"
                    ></b-form-input>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
          </section>
          <!-- Closes the Order Tracking section only; without it the Receiver
               divider above was drawn twice. -->
          <div class="divider mt-2 mb-4" v-if="isAddressCa"></div>
          <section v-if="showItemsSection">
            <!-- DP-4513: the section is titled like Sender/Receiver in the
                 new design; intl vs domestic now only changes the columns. -->
            <h6 class="text-uppercase mb-3">Items</h6>

            <div v-if="itemsLoading" class="dp-loading mb-3">
              <b-spinner small=""></b-spinner><span class="ml-2">Loading items…</span>
            </div>

            <template v-if="!itemsLoading">
              <div class="dp-customs-summary mb-3" v-if="customsItems.length">
                <div class="dp-cs-cell">
                  <div class="dp-cs-label">Items</div>
                  <!-- DP-4478: how many of the order's items go in this parcel. -->
                  <div class="dp-cs-value">
                    {{ selectedItems.length }} <small>of {{ customsItems.length }}</small>
                  </div>
                </div>
                <div class="dp-cs-cell">
                  <!-- DP-4624 — Paul, 2026-08-20: the two labels were the wrong
                       way round. "(ex GST)" belongs on the NZ/INT figure, which
                       really is GST-exclusive (DP-4304); an AU shipment shows
                       what the line was charged, so it must not claim to be. -->
                  <div class="dp-cs-label">
                    {{ showCustomsColumns ? 'Item value (ex GST)' : 'Item value' }}
                  </div>
                  <div class="dp-cs-value">
                    A${{ totalValue }}
                    <!-- DP-4305: INT/NZ orders are priced in another currency and
                         converted to AUD (ex GST, DP-4304) for the declaration.
                         The arithmetic is spelled out so the figure can be
                         checked against the order without guesswork. Only shown
                         when every line shares one currency — a mixed-currency
                         order has no single "original" total. -->
                    <small v-if="needsFxConversion && itemCurrencies.length === 1">
                      {{ itemCurrencies[0] }} {{ totalValueOriginal }}
                      <template v-if="hasAllFxRates">
                        × {{ Number(fxRates[itemCurrencies[0]]).toFixed(4) }}
                      </template>
                    </small>
                  </div>
                </div>
                <div class="dp-cs-cell">
                  <div class="dp-cs-label">Items weight</div>
                  <div class="dp-cs-value">{{ itemsTotalWeight }} <small>kg</small></div>
                </div>
                <div class="dp-cs-cell" v-if="showCustomsColumns">
                  <div class="dp-cs-label">Duties</div>
                  <div class="dp-cs-value">
                    <span v-if="dutiesReceiverPays" class="dp-duty dp-duty--receiver"
                      ><span class="dp-dot"></span> Receiver pays</span
                    >
                    <span v-else class="dp-duty dp-duty--prepaid"
                      ><span class="dp-dot"></span> Prepaid · Zonos</span
                    >
                  </div>
                </div>
                <div class="dp-cs-cell" v-else>
                  <div class="dp-cs-label">Parcel weight</div>
                  <div class="dp-cs-value">{{ form.weight.value }} <small>kg</small></div>
                </div>
              </div>

              <!-- DP-4305: the exchange rate the declaration is based on. -->
              <p class="dp-fx mb-3" v-if="needsFxConversion">
                <template v-if="fxLoading">
                  <b-spinner small=""></b-spinner>
                  <span class="ml-2">Getting today's exchange rate…</span>
                </template>
                <template v-else-if="hasAllFxRates">
                  <span class="dp-fx-dot"></span>
                  Values converted to AUD at <b>{{ fxRateLabel }}</b>
                </template>
                <template v-else-if="fxFailed">
                  <span class="dp-fx-dot dp-fx-dot--warn"></span>
                  Live exchange rate unavailable — the declaration will use the
                  server's fallback rate.
                  <a href="#" @click.prevent="fetchFxRate">Retry</a>
                </template>
              </p>

              <b-alert
                :show="showCustomsColumns && hasMissingHs"
                variant="danger"
                class="mb-3"
              >
                <b
                  >{{ missingHsCount }} item{{ missingHsCount === 1 ? '' : 's' }}
                  {{ missingHsCount === 1 ? 'has' : 'have' }} no HS code</b
                >
                and can't be shipped internationally. Add the HS code before generating
                the label.
              </b-alert>

              <!-- DP-4672 — Hakim, 2026-08-25: only Service Centre AU's AusPost
                   account is connected to Zonos, so a US label raised anywhere
                   else bills an account Zonos cannot see and the duties are
                   never collected. Shown as soon as the destination is US/PR,
                   so staff switch outlet before spending time on the form
                   rather than being stopped at Get Quotes. -->
              <b-alert :show="requiresZonosOutletSwitch" variant="danger" class="mb-3">
                {{ zonosOutletMessage }}
              </b-alert>

              <!-- DP-4672: the US declares a 10-digit HTSUS code. We hold one
                   only for the classes fulfilment has signed off, and a padded
                   guess is a false customs declaration — so the label is
                   blocked and the items are named. -->
              <b-alert :show="hasMissingUsHs" variant="danger" class="mb-3">
                <b
                  >No 10-digit US tariff code for
                  {{ missingUsHsItems.join(', ') }}.</b
                >
                Australia Post rejects a US shipment without one. Ask fulfilment to
                classify {{ missingUsHsItems.length === 1 ? 'it' : 'them' }} before
                shipping this order to the US.
              </b-alert>

              <!-- DP-4466: goods over A$2,000 need an Export Declaration Number
                   (ABF), which is unsupported — the label must not be created
                   here. Same guard exists server-side. -->
              <b-alert :show="exceedsEdnThreshold" variant="danger" class="mb-3">
                <b>This shipment value is over A${{ ednThresholdLabel }}.</b>
                Australia Post requires an Export Declaration Number for international
                shipments over A${{ ednThresholdLabel }}. Please process this shipment
                directly in the Australia Post portal.
              </b-alert>

              <!-- DP-4513: declaration values in the new card design — one
                   light-blue card per item, the resolved values in white
                   boxes under floating column heads.
                   DP-4478 — Hakim, 2026-08-06: the checkbox is back, and this
                   time it works. DP-4471 (Paul, 2026-08-05) removed item
                   selection and DP-4611 (Paul, 2026-08-18) removed the dead
                   checkbox, because a label for some items left the rest with
                   no way to ship. Each label now records its items, so the
                   rest can go on a later label (Manage Shipping). Items an
                   earlier label carried show "Dispatched" and start unticked;
                   staff can tick one to ship it again. -->
              <div class="mb-2" v-if="customsItems.length">
                <div
                  class="dp-decl-grid dp-decl-head"
                  :class="showCustomsColumns ? 'dp-decl-grid--intl' : 'dp-decl-grid--domestic'"
                >
                  <span></span>
                  <span></span>
                  <!-- DP-4672: a US parcel declares the 10-digit HTSUS code, not
                       our 6-digit HS code, so the column says which one is on
                       screen. Staff read this to check what we are declaring. -->
                  <span v-if="showCustomsColumns">{{ hsColumnLabel }}</span>
                  <span v-if="showCustomsColumns">Customs Description</span>
                  <span v-if="showCustomsColumns">Origin</span>
                  <span>Weight</span>
                </div>
                <div
                  v-for="item in customsItems"
                  :key="item.order_item_id"
                  class="dp-decl-grid dp-decl-row"
                  :class="[
                    showCustomsColumns ? 'dp-decl-grid--intl' : 'dp-decl-grid--domestic',
                    {
                      'dp-decl-row--missing':
                        showCustomsColumns && isSelected(item) && isMissingDeclaredCode(item),
                      'dp-decl-row--unselected': !isSelected(item),
                    },
                  ]"
                >
                  <div class="dp-decl-check">
                    <input
                      type="checkbox"
                      class="dp-check"
                      :checked="isSelected(item)"
                      :aria-label="`Ship ${item.name}`"
                      @change="toggleItem(item)"
                    />
                  </div>
                  <div class="dp-decl-item" @click="toggleItem(item)">
                    <div class="dp-decl-name">
                      <span class="dp-item-name">{{ item.name }}</span>
                      <span v-if="item.quantity > 1" class="dp-qty">×{{ item.quantity }}</span>
                      <!-- DP-4473/DP-4519: glasses type + frame shape + colour,
                           so identically named glasses lines can be told apart
                           at a glance. -->
                      <span class="dp-item-frame" v-if="frameLabel(item)">{{
                        frameLabel(item)
                      }}</span>
                      <!-- DP-4313: the item's own id, so staff can tell apart
                           two items with the same name and attributes. -->
                      <span class="dp-item-id">Order ID: {{ item.order_item_id }}</span>
                    </div>
                    <!-- DP-4478: an earlier label already carried this item. -->
                    <span v-if="item.dispatched" class="dp-dispatched">Dispatched</span>
                  </div>
                  <div v-if="showCustomsColumns">
                    <span v-if="declaredCode(item)" class="dp-decl-box dp-decl-box--hs">{{
                      declaredCode(item)
                    }}</span>
                    <span v-else class="dp-decl-box dp-decl-box--missing">{{ missingCodeLabel }}</span>
                  </div>
                  <div v-if="showCustomsColumns">
                    <span class="dp-decl-box" :title="item.customs_description || ''">{{
                      item.customs_description || '—'
                    }}</span>
                  </div>
                  <div v-if="showCustomsColumns">
                    <span class="dp-decl-box">{{ countryMeta(item.country_of_origin).name }}</span>
                  </div>
                  <div>
                    <span class="dp-decl-box dp-decl-box--wt">{{
                      item.weight != null ? (item.weight * item.quantity).toFixed(3) + ' kg' : '—'
                    }}</span>
                  </div>
                </div>
              </div>
              <div v-if="!customsItems.length" class="text-muted mb-2">
                No shippable items found for this order.
              </div>
              <!-- DP-4478: "at least one item must be selected before Get Quotes". -->
              <p v-else-if="!selectedItems.length" class="dp-select-hint mb-2">
                Select at least one item to ship.
              </p>

              <!-- Service lines (postage, warranties) are paid for but are not
                   goods, so they are not declared. Shown explicitly so the gap
                   between the declared value and the order total is obvious. -->
              <p class="dp-excluded mb-2" v-if="showCustomsColumns && excludedItems.length">
                Not declared (not goods):
                <span v-for="(item, index) in excludedItems" :key="item.order_item_id">
                  {{ item.name }} {{ formatMoney(item.value_original, item.currency)
                  }}<template v-if="index < excludedItems.length - 1">, </template>
                </span>
              </p>
            </template>
          </section>
          <div class="divider mt-2 mb-4" v-if="showItemsSection"></div>
          <section v-if="!isAddressCa">
            <h6 class="text-uppercase mb-3">Parcel</h6>
            <b-row>
              <b-col cols="6" v-if="!isAddressDomestic && !showItemsSection">
                <b-form-group label="Description" label-for="description">
                  <b-form-input
                    id="description"
                    v-model="form.description"
                    autofocus=""
                    required=""
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="Delivery instructions" label-for="delivery-instructions">
                  <b-form-select
                    v-model="form.receiver.instructions"
                    name="delivery-instructions"
                    required=""
                  >
                    <b-form-select-option
                      v-for="option in deliveryInstructions"
                      :key="option.id"
                      :value="option.value"
                      >{{ option.text }}</b-form-select-option
                    >
                  </b-form-select>
                </b-form-group>
              </b-col>
              <!-- DP-4297 + DP-4325: one weight goes to AusPost and it must be
                   the real parcel weight (selected items + packaging). It is
                   calculated by default and staff can overwrite it. -->
              <b-col cols="6">
                <b-form-group
                  :label="showItemsSection ? 'Total shipment weight (kg)' : 'Weight (kg)'"
                  label-for="weight"
                >
                  <b-form-input
                    name="weight"
                    id="weight"
                    v-model="form.weight.value"
                    type="number"
                    step="0.001"
                    min="0.001"
                    :state="weightIsValid ? null : false"
                    required=""
                    @input="onWeightEdited"
                  ></b-form-input>
                  <b-form-invalid-feedback :state="weightIsValid">
                    Enter a shipment weight greater than 0 (for example 0.130).
                  </b-form-invalid-feedback>
                  <b-form-text v-if="showItemsSection">
                    <span v-if="!weightOverridden">
                      Calculated from items ({{ itemsTotalWeight }} kg) + parcel
                      ({{ parcelTareWeight.toFixed(3) }} kg). You can edit this if needed.
                    </span>
                    <span v-else>
                      Manually set. Calculated value is {{ calculatedTotalWeight }} kg —
                      <a href="#" @click.prevent="resetCalculatedWeight">reset</a>.
                    </span>
                  </b-form-text>
                </b-form-group>
              </b-col>
              <b-col cols="6" v-if="!showItemsSection">
                <b-form-group label="Volume (m3)" label-for="volume">
                  <b-form-input
                    id="volume"
                    v-model="form.volume.value"
                    type="number"
                    step="0.00001"
                    required=""
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="6" v-if="!isAddressDomestic && !showItemsSection">
                <b-form-group label="Country of origin" label-for="country-of-origin">
                  <b-form-input
                    id="country-of-origin"
                    v-model="form.contents.country_of_origin"
                    required=""
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="6" v-if="!showItemsSection">
                <b-form-group label="Value (AUD$)" label-for="value">
                  <b-form-input
                    name="value"
                    v-model="form.contents.value"
                    type="number"
                    required=""
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="Parcel Size" label-for="parcel-size">
                  <b-form-select
                    v-model="form.parcel_size_id"
                    name="parcel-size"
                    required=""
                    :options="parcelSizes"
                    @change="parcelOnChange"
                  ></b-form-select>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="Parcel Quantity (no. of boxes)" label-for="quantity">
                  <b-form-select
                    v-model="form.parcel_quantity"
                    name="parcel-size"
                    required=""
                    :options="parcelQuantities"
                  ></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>
          </section>
        </div>
        <div class="container" v-if="step === 2">
          <CourierPicker
            :recipient="toBeSubmittedAddress"
            :order-invoice="orderInvoice"
            :parcel="form"
            :selected-outlet-address="selectedOutletAddress"
            :couriers="couriers"
            v-model="courier"
          ></CourierPicker>
          <b-alert :show="errors.length &gt; 0" variant="danger" dismissible="dismissible">
            <h5 class="alert-heading">Create Order Error</h5>
            <!-- DP-4612 round 2: these messages are written as several short
                 lines ("what is wrong" / "what to do"), and HTML collapses the
                 newlines into one run-on sentence without this. -->
            <div class="dp-multiline" v-for="(error, index) of errors" v-bind:key="index">
              {{ error.message }}
            </div>
          </b-alert>
          <b-alert
            :show="Object.keys(quoteWarnings).length &gt; 0"
            variant="warning"
            dismissible="dismissible"
          >
            <div v-if="quoteWarnings">
              <h5 class="alert-heading">Quote Warnings</h5>
              <div class="dp-multiline" v-for="(warning, index) of quoteWarnings" v-bind:key="index">
                {{ warning }}
              </div>
            </div>
          </b-alert>
        </div>
      </div>
      <template v-slot:modal-footer="{ cancel }">
        <div class="flex-fill m-0">
          <div class="container">
            <div class="d-flex">
              <b-button class="mr-3" v-show="step === 2" variant="primary" @click="step = 1"
                >Back</b-button
              >
              <b-button class="mr-3" variant="outline-light" @click="cancel()">Cancel</b-button>
              <b-button
                class="mr-3"
                variant="primary"
                v-show="step === 2"
                @click="createOrder"
                :disabled="isLoading || !courier"
              >
                <b-spinner v-if="isLoading" small=""></b-spinner><span class="ml-1">Confirm</span>
              </b-button>
              <b-button
                variant="primary"
                v-show="step === 1"
                @click="passes(getQuote)"
                :disabled="isLoading || !selectedAddress || !canSubmitItems"
              >
                <b-spinner v-if="isLoading" small=""></b-spinner
                ><span class="ml-1">Get Quotes</span>
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import apiOrders from '@/api/orders';
import apiFx from '@/api/fx';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import _ from 'lodash';
import CourierPicker from '@/views/modals/CreateShippingOrder/CourierPicker.vue';

// DP-4609: outlet regions allowed to ship an order's region. AU outlets also
// handle INT and NZ orders; regions not listed must match their own outlet.
const allowedSenderRegions = {
  AU: ['AU'],
  CA: ['CA'],
  NZ: ['NZ', 'AU'],
  INT: ['INT', 'AU'],
};
// DP-4672: destinations whose duties Zonos settles. Fallback only — the real
// list, and the one outlet whose AusPost account is linked to Zonos, come from
// the customs endpoint (`zonos`), so this popup and the payload rule read the
// same config and cannot drift apart.
//
// ⚠ DP-4616 used this list to refuse US/PR outright, because the US wants a
// 10-digit tariff code we did not hold. Hakim signed the codes off on
// 2026-09-10, so the refusal is now an outlet gate instead.
const ZONOS_COUNTRIES = ['US', 'PR'];

export default {
  name: 'CreateShippingOrderModal',
  components: {
    ValidationProvider,
    ValidationObserver,
    CourierPicker,
  },
  data() {
    const deliveryInstructions = [
      {
        id: 1,
        value: 'authority_to_leave',
        text: 'Authority to leave',
      },
      {
        id: 2,
        value: 'signature_on_delivery',
        text: 'Signature on delivery',
      },
      {
        id: 3,
        value: 'authority_to_leave_signature',
        text: 'Authority to leave signature',
      },
    ];
    const couriers = [];
    const parcelSizes = [
      {
        value: 1,
        text: 'Small Box (18cm x 13cm x 6cm)',
        length: 18,
        width: 13,
        height: 6,
        weight: '0.2',
        units: 'kg',
      },
      {
        value: 2,
        text: 'Large Box (24cm x 15cm x 6cm)',
        length: 24,
        width: 15,
        height: 6,
        weight: '0.3',
        units: 'kg',
      },
    ];
    const parcelQuantities = _.range(1, 10).map(quantity => ({
      value: quantity,
      text: `${quantity} Box`,
    }));
    return {
      quoteWarnings: {},
      errors: [],
      step: 1,
      key: 0,
      isLoading: false,
      courier: null,
      couriers,
      // DP-4137: shippable items + their resolved customs data.
      customsItems: [],
      // DP-4478: the order_item ids ticked for THIS label. Only these are
      // declared, weighed, valued and sent to Australia Post. (DP-4471 had
      // removed selection; DP-4478 restores it with each label's items saved.)
      selectedItemIds: [],
      // Paid-for lines that are not goods (postage, warranties). Never
      // declared; listed so staff can see why the declared value is below the
      // order total.
      excludedItems: [],
      itemsLoading: false,
      // DP-4466: international shipments declaring more than this need an
      // Export Declaration Number (unsupported) — the label is blocked.
      exportDeclaration: {
        threshold_aud: 2000,
      },
      // DP-4672: the Zonos destinations and the only outlet whose AusPost
      // account is connected to them. Served by the customs endpoint; these are
      // the fallbacks used before it answers.
      zonos: {
        countries: ZONOS_COUNTRIES,
        outlet_id: null,
        outlet_name: 'Service Centre AU',
        outlet_message: '',
      },
      // DP-4305: the currency the order was priced in (INT = USD, NZ = NZD),
      // and the live rate used to declare its value in AUD. `fxFailed` means
      // both rate providers were unreachable and the backend's configured
      // fallback rate will be used instead.
      orderCurrency: 'AUD',
      valueCurrency: 'AUD',
      // One rate per currency present in the order: prices are per region, so
      // an order can carry lines priced in different currencies.
      fxRates: {},
      // `fxSource` is shown to staff (a plain name, never a URL); `fxProvider`
      // is the host, sent to the backend for the audit log only.
      fxSource: null,
      fxProvider: null,
      fxDate: null,
      fxLoading: false,
      fxFailed: false,
      // DP-4297: packaging tare weights, served by the backend so the box
      // weights are not duplicated in the UI.
      packaging: {
        tare_weight: {},
        default_tare_weight: 0.05,
        min_parcel_weight: 0.001,
      },
      // DP-4325: true once staff types their own total shipment weight, which
      // then stops being recalculated.
      weightOverridden: false,
      senderOutletId: null,
      deliveryInstructions,
      parcelSizes,
      parcelQuantities,
      form: {
        first_mile_option: 'pickup',
        description: null,
        ca_order_tracking: null,
        parcel_size_id: 1,
        parcel_quantity: 1,
        weight: {
          units: 'kg',
          value: '0.3',
        },
        volume: {
          units: 'm3',
          value: '0',
        },
        sender: {
          contact: {
            name: 'Dresden Vision',
          },
          outlet: null,
        },
        receiver: {
          contact: {
            name: null,
            email: null,
            phone: null,
          },
          address: {
            address_line1: null,
            address_line2: null,
            suburb: null,
            state_name: null,
            postcode: null,
            country: null,
          },
          instructions: deliveryInstructions[0].value,
        },
        contents: {
          description: null,
          value: null,
          country_of_origin: null,
        },
      },
    };
  },
  computed: {
    ...mapState('auth', {
      currentUser: state => state.user,
      selectedOutletId: state => state.selectedOutletId,
    }),
    ...mapState({
      outlets: state => state.outlets,
      regions: state => state.regions,
    }),
    ...mapGetters({
      selectedOrder: 'selectedOrder',
      selectedAddress: 'addressModal/selectedBillingAddress',
      allAddresses: 'addressModal/allCurrentAddresses',
    }),
    ...mapGetters('auth', ['locationSelectorOutlets']),
    outletsWithAddress() {
      return this.locationSelectorOutlets.filter(outlet => outlet.address !== null);
    },
    sortedAddresses() {
      return this._.orderBy(this.allAddresses, ['updated_at'], ['desc']);
    },
    toBeSubmittedAddress() {
      if (!this.selectedAddress) return {};

      const contact = {
        name: `${this.selectedAddress.first_name} ${this.selectedAddress.last_name}`,
        email: this.selectedAddress.email,
        phone: this.selectedAddress.mobile,
      };

      const address = {
        ...this.selectedAddress.address,
      };

      if (!this._.get(address, 'suburb')) {
        address.suburb = address.city;
      }

      return {
        contact,
        address,
      };
    },
    customer() {
      return this._.get(this.selectedOrder, 'customer');
    },
    customerId() {
      return this._.get(this.customer, 'id');
    },
    billingInfo() {
      return this._.get(this.selectedOrder, 'order_billing');
    },
    billingAddress() {
      return this._.get(this.billingInfo, 'billing_address.address');
    },
    deliveryAddresses() {
      const customerBillingAddresses = this._.get(
        this.selectedOrder,
        'customer.billing_addresses',
        [],
      );
      const customerAddresses = customerBillingAddresses.map(billing =>
        this._.get(billing, 'address'),
      );
      return this.billingAddress ? [this.billingAddress, ...customerAddresses] : customerAddresses;
    },
    orderInvoice() {
      return this._.get(this.selectedOrder, 'vend_invoice_number');
    },
    selectedOutletAddress() {
      const address = this._.get(this.form, 'sender.outlet.address');
      return address ? this.getAddress(address) : '';
    },
    isAddressDomestic() {
      return this._.get(this.selectedAddress, 'address.country') === 'AU';
    },
    isAddressCa() {
      const selectedOutletRegionId = this.outlets.find(
        outlet => outlet.id === this.selectedOutletId,
      ).region_id;

      return (
        this.regions.find(region => region.id === selectedOutletRegionId).country_code === 'CA'
      );
    },
    orderRegionCode() {
      const regionId = this._.get(this.selectedOrder, 'origin_outlet.region_id');
      return this._.get(
        this.regions.find(region => region.id === regionId),
        'country_code',
        null,
      );
    },
    senderRegionCode() {
      const senderOutletId = this._.get(this.form, 'sender.outlet.id');
      const senderOutlet = this.outlets.find(outlet => outlet.id === senderOutletId);
      if (!senderOutlet) return null;
      return this._.get(
        this.regions.find(region => region.id === senderOutlet.region_id),
        'country_code',
        null,
      );
    },
    isUserAdmin() {
      return this.currentUser.role_id === 1;
    },
    // DP-4137 + DP-4280: item selection applies to every AusPost label —
    // international AND AU domestic. Shown once a receiver address is chosen;
    // hidden for CA staff regions (Canada uses manual entry, no AusPost).
    showItemsSection() {
      return !!this.selectedAddress && !this.isAddressCa;
    },
    // DP-4280: HS code, customs description, origin and duties are
    // international-only. Domestic staff just pick what goes in the box.
    showCustomsColumns() {
      return this.showItemsSection && !this.isAddressDomestic;
    },
    // DP-4478: the items ticked for this label, in the order they are listed.
    selectedItems() {
      return this.customsItems.filter(item => this.isSelected(item));
    },
    // DP-4478: only a ticked item can block the label — an unticked item with
    // no HS code is not going in this parcel.
    hasMissingHs() {
      return this.selectedItems.some(item => item.missing_hs);
    },
    canSubmitItems() {
      if (!this.showItemsSection) return this.weightIsValid;
      if (this.selectedItems.length === 0) return false;
      if (this.showCustomsColumns && this.hasMissingHs) return false;
      // DP-4672: no US label without the Zonos outlet and a 10-digit code for
      // every item.
      if (this.requiresZonosOutletSwitch) return false;
      if (this.hasMissingUsHs) return false;
      // DP-4466: over the EDN threshold the label must not be created here.
      if (this.exceedsEdnThreshold) return false;

      return this.weightIsValid;
    },
    // DP-4304/DP-4305: values from the API are GST-exclusive; `value` carries
    // the backend's fallback conversion, `value_original` the order's own
    // currency. When a live rate is available we declare from that instead.
    // DP-4478: value, weight and the A$2,000 check cover the ticked items only.
    totalValue() {
      return this.selectedItems
        .reduce((sum, item) => sum + this.itemValueAud(item), 0)
        .toFixed(2);
    },
    totalValueOriginal() {
      return this.selectedItems
        .reduce((sum, item) => sum + Number(item.value_original || 0), 0)
        .toFixed(2);
    },
    // DP-4466: A$ threshold above which AusPost requires an Export Declaration
    // Number. International only — served by the customs endpoint so the rule
    // lives in one place (0 disables the guard).
    ednThresholdAud() {
      return Number(this._.get(this.exportDeclaration, 'threshold_aud', 2000));
    },
    ednThresholdLabel() {
      return this.ednThresholdAud.toLocaleString('en-AU');
    },
    exceedsEdnThreshold() {
      return (
        this.showCustomsColumns &&
        this.ednThresholdAud > 0 &&
        Number(this.totalValue) > this.ednThresholdAud
      );
    },
    // DP-4305: the currencies actually present on the items — the price came
    // from a region's catalogue, so this is per line, not per order.
    itemCurrencies() {
      const currencies = this.customsItems
        .map(item => (item.currency || this.orderCurrency || 'AUD').toUpperCase())
        .filter(currency => currency !== (this.valueCurrency || 'AUD'));

      return [...new Set(currencies)];
    },
    needsFxConversion() {
      return this.showCustomsColumns && this.itemCurrencies.length > 0;
    },
    hasAllFxRates() {
      return this.itemCurrencies.every(currency => Number(this.fxRates[currency]) > 0);
    },
    fxRateLabel() {
      if (!this.hasAllFxRates) return '';
      const rates = this.itemCurrencies
        .map(currency => `1 ${currency} = ${Number(this.fxRates[currency]).toFixed(4)} AUD`)
        .join(', ');
      const suffix = this.fxDate ? ` · ${this.fxSource} ${this.fxDate}` : ` · ${this.fxSource}`;

      return `${rates}${suffix}`;
    },
    itemsTotalWeight() {
      return this.selectedItems
        .reduce((sum, item) => sum + Number(item.weight || 0) * Number(item.quantity || 1), 0)
        .toFixed(3);
    },
    // DP-4297: packaging weight of the chosen parcel size.
    parcelTareWeight() {
      const tare = this._.get(this.packaging, ['tare_weight', this.form.parcel_size_id]);
      return Number(tare != null ? tare : this._.get(this.packaging, 'default_tare_weight', 0.05));
    },
    // DP-4297: what AusPost must be told the parcel weighs.
    calculatedTotalWeight() {
      return (Number(this.itemsTotalWeight) + this.parcelTareWeight).toFixed(3);
    },
    // DP-4325: an emptied or non-positive override must block the label.
    weightIsValid() {
      const weight = Number(this.form.weight.value);
      const min = Number(this._.get(this.packaging, 'min_parcel_weight', 0.001));

      return Number.isFinite(weight) && this.form.weight.value !== '' && weight >= min;
    },
    missingHsCount() {
      return this.selectedItems.filter(item => item.missing_hs).length;
    },
    destinationCountryCode() {
      return (this._.get(this.selectedAddress, 'address.country') || '').toUpperCase();
    },
    // US & Puerto Rico are settled by Zonos (SENDER_PAYS_ZONOS); everyone else
    // is RECEIVER_PAYS.
    dutiesReceiverPays() {
      return !this.isZonosDestination;
    },
    zonosCountries() {
      const countries = this._.get(this.zonos, 'countries');

      return Array.isArray(countries) && countries.length ? countries : ZONOS_COUNTRIES;
    },
    isZonosDestination() {
      return this.zonosCountries.includes(this.destinationCountryCode);
    },
    // DP-4672 — Hakim, 2026-08-25: only one AusPost account is connected to
    // Zonos (MLID 34UYY, sub-account 4672018, Service Centre AU). A label raised
    // from any other outlet bills an account Zonos has never heard of, so the
    // duties are never collected and the parcel stops at the US border.
    // Null until the customs endpoint answers. Until then we do not know which
    // outlet is the Zonos one, and guessing would flash "switch to Service
    // Centre AU" at somebody who is already standing in it.
    zonosOutletId() {
      const id = Number(this._.get(this.zonos, 'outlet_id'));

      return Number.isFinite(id) && id > 0 ? id : null;
    },
    senderOutletIsZonosLinked() {
      return Number(this._.get(this.form, 'sender.outlet.id')) === this.zonosOutletId;
    },
    // Deliberately false while the outlet id is unknown: the popup's job is to
    // save staff a wasted trip through the form, not to be the safety net.
    // `AuPostOrderController::store()` and `getAuPostQuote()` refuse the
    // shipment either way, so nothing gets through on a slow response.
    requiresZonosOutletSwitch() {
      if (this.zonosOutletId === null) return false;

      return this.isZonosDestination && !this.senderOutletIsZonosLinked;
    },
    zonosOutletMessage() {
      const outlet = this._.get(this.zonos, 'outlet_name') || 'Service Centre AU';

      return (
        this._.get(this.zonos, 'outlet_message')
        || `US shipments can only be created using the ${outlet} outlet, as this is the only `
          + `AusPost account connected to Zonos. Please switch to ${outlet} before creating the `
          + 'shipment.'
      );
    },
    // DP-4672: the US declares a 10-digit HTSUS code, not our 6-digit HS code.
    // We hold one only for the classes fulfilment has signed off, so an item
    // without one is blocked here rather than declared with a padded guess —
    // 9004900000 and 6307900000 are not codes that exist.
    missingUsHsItems() {
      if (!this.isZonosDestination) return [];

      return this.selectedItems.filter(item => !item.hs_code_us).map(item => item.name);
    },
    // DP-4672: which code this parcel actually declares. US/PR send the
    // 10-digit HTSUS code; everyone else sends our 6-digit HS code and always
    // has. Label and value are driven off the same flag so they cannot disagree.
    hsColumnLabel() {
      return this.isZonosDestination ? 'US Tariff Code' : 'HS Code';
    },
    missingCodeLabel() {
      return this.isZonosDestination ? '● No US tariff code' : '● No HS code';
    },
    hasMissingUsHs() {
      return this.missingUsHsItems.length > 0;
    },
  },
  watch: {
    // DP-4297 + DP-4325: keep the shipment weight in step with the items and
    // the parcel size, unless staff has taken it over.
    'form.parcel_size_id': function parcelSizeChanged() {
      this.applyCalculatedWeight();
    },
    customsItems() {
      this.applyCalculatedWeight();
    },
    // DP-4478: ticking or unticking an item changes what the parcel weighs.
    selectedItemIds() {
      this.applyCalculatedWeight();
    },
    // Switching between a domestic and an international address changes which
    // rules apply, so the calculated weight is re-derived.
    showItemsSection() {
      this.applyCalculatedWeight();
    },
    // DP-4305: the destination decides whether a customs value is declared at
    // all, and it can be chosen after the items have loaded.
    showCustomsColumns(shows) {
      if (shows && !this.hasAllFxRates && !this.fxLoading) this.fetchFxRate();
    },
  },
  methods: {
    ...mapMutations({
      updateJob: 'UPDATE_JOB_IN_JOBS',
    }),
    // DP-4297: default the AusPost weight to items + packaging.
    applyCalculatedWeight() {
      if (!this.showItemsSection || this.weightOverridden) return;
      this.form.weight.value = this.calculatedTotalWeight;
    },
    // DP-4325: any manual edit wins until it is explicitly reset.
    onWeightEdited() {
      if (!this.showItemsSection) return;
      if (this.form.weight.value !== this.calculatedTotalWeight) {
        this.weightOverridden = true;
      }
    },
    resetCalculatedWeight() {
      this.weightOverridden = false;
      this.form.weight.value = this.calculatedTotalWeight;
    },
    getAddress(obj) {
      const segments = [
        obj.address1,
        obj.address2,
        obj.suburb,
        obj.city,
        obj.state,
        obj.country === 'AU' ? 'Australia' : obj.country,
      ].filter(segment => segment);

      return segments.join(', ');
    },
    onShow() {
      this.warnings = {};
      this.errors = [];
      this.form.ca_order_tracking = null;
      this.step = 1;
      this.form.volume.value = '0';
      this.form.weight.value = '0.3';
      this.form.parcel_size_id = 1;
      // DP-4325: every open starts from the calculated weight again.
      this.weightOverridden = false;
      this.form.receiver.instructions = this.deliveryInstructions[0].value;
      let nextDay = this.$moment();
      do {
        nextDay = nextDay.add(1, 'days');
      } while (nextDay.weekday() >= 5 || nextDay.weekday() === 0);
      this.form.description = 'Prescription glasses';
      this.form.sender.outlet = this.locationSelectorOutlets.find(
        outlet => outlet.id === this.selectedOutletId,
      );
      this.form.contents.country_of_origin = 'Australia';
      this.form.contents.value = Math.floor(this.selectedOrder.total_amount);
      this.fetchAuPostCustomsItems();
    },
    // DP-4137: load the order's shippable items with their resolved customs
    // data (HS code, description, weight, origin).
    // DP-4478: and whether an earlier label already carried each one. Items
    // not shipped yet start ticked, items already dispatched start unticked
    // (Figma frames 1576 / 1593 / 1611).
    async fetchAuPostCustomsItems() {
      this.customsItems = [];
      this.selectedItemIds = [];
      this.excludedItems = [];
      const orderId = this._.get(this.selectedOrder, 'id');
      if (!orderId) return;
      this.itemsLoading = true;
      try {
        const response = await apiOrders.getAuPostCustomsItems(orderId);
        this.customsItems = this._.get(response, 'data.items', []);
        this.selectedItemIds = this.customsItems
          .filter(item => !item.dispatched)
          .map(item => item.order_item_id);
        this.excludedItems = this._.get(response, 'data.excluded_items', []);
        // DP-4305 currencies + DP-4297 packaging weights + DP-4466 threshold.
        this.orderCurrency = this._.get(response, 'data.currency', 'AUD');
        this.valueCurrency = this._.get(response, 'data.value_currency', 'AUD');
        this.packaging = this._.get(response, 'data.packaging', this.packaging);
        this.exportDeclaration = this._.get(
          response,
          'data.export_declaration',
          this.exportDeclaration,
        );
        // DP-4672: the Zonos country list and the Zonos-connected outlet.
        this.zonos = this._.get(response, 'data.zonos', this.zonos);
      } catch (error) {
        this.customsItems = [];
      } finally {
        this.itemsLoading = false;
        this.applyCalculatedWeight();
      }
      // Needs orderCurrency + the destination, so it runs after the items load.
      this.fetchFxRate();
    },
    isSelected(item) {
      return this.selectedItemIds.includes(item.order_item_id);
    },
    toggleItem(item) {
      this.selectedItemIds = this.isSelected(item)
        ? this.selectedItemIds.filter(id => id !== item.order_item_id)
        : [...this.selectedItemIds, item.order_item_id];
    },
    // DP-4672: the tariff code actually going on this parcel's customs form.
    // Australia Post refuses a US label carrying our 6-digit code, so for US/PR
    // the popup must show the 10-digit one rather than a number we are not
    // sending — otherwise staff check the screen, see a code, and the label
    // still fails. Every other destination is unchanged.
    declaredCode(item) {
      return this.isZonosDestination ? item.hs_code_us : item.hs_code;
    },
    // Flags the rows that will block the label. On a US parcel a row can hold a
    // perfectly good HS6 and still have no US code, which is exactly the case
    // the old `missing_hs` check could not see.
    isMissingDeclaredCode(item) {
      return this.isZonosDestination ? !item.hs_code_us : Boolean(item.missing_hs);
    },
    // DP-4305: the AUD value we declare for an item — live rate for that
    // item's own currency when we have one, otherwise whatever the backend
    // converted with its configured rate.
    itemValueAud(item) {
      // DP-4612: a warranty replacement declares a flat nominal value set by
      // policy, not a converted sale price. Its line is charged at 0.00, so
      // running it through any rate gives 0.00 — the screen would show nothing
      // while the label declared A$1.00. Take the backend's figure as it is.
      if (item.declared_value_is_fixed) {
        return Number(item.value || 0);
      }

      const currency = (item.currency || this.orderCurrency || 'AUD').toUpperCase();
      const rate = Number(this.fxRates[currency]);
      if (rate > 0) {
        return Math.round(Number(item.value_original || 0) * rate * 100) / 100;
      }

      return Number(item.value || 0);
    },
    // DP-4305: fetch a rate for every currency on the order. Never blocks the
    // label — on failure the backend falls back to its configured rate.
    async fetchFxRate() {
      this.fxRates = {};
      this.fxSource = null;
      this.fxProvider = null;
      this.fxDate = null;
      this.fxFailed = false;
      if (!this.needsFxConversion) return;

      this.fxLoading = true;
      try {
        const results = await Promise.all(
          this.itemCurrencies.map(currency => apiFx.getRateToAud(currency)),
        );

        const rates = {};
        results.forEach((result, index) => {
          if (!result) return;
          rates[this.itemCurrencies[index]] = result.rate;
          // Every currency comes from the same provider chain, so the last
          // answer describes the set.
          this.fxSource = result.source;
          this.fxProvider = result.provider;
          this.fxDate = result.date;
        });
        this.fxRates = rates;
        this.fxFailed = !this.hasAllFxRates;
      } finally {
        this.fxLoading = false;
      }
    },
    // DP-4305: the converted values staff can see, keyed by order item id, so
    // the label declares exactly what was on screen.
    customsValuePayload() {
      if (!this.needsFxConversion || !this.hasAllFxRates) return {};

      const customsValues = {};
      this.selectedItems.forEach(item => {
        customsValues[item.order_item_id] = this.itemValueAud(item);
      });

      return {
        customs_values: customsValues,
        fx_rates: this.fxRates,
        fx_source: this.fxSource,
        fx_provider: this.fxProvider,
        fx_rate_date: this.fxDate,
      };
    },
    // DP-4473/DP-4519: "Blue Light Filter · Original · Black" under the item
    // name — identically named glasses lines (every DresdenGo item is
    // "DRESDENGO Glasses") need the glasses type and the frame's shape and
    // colour to be told apart. Lenses-only lines lead with where the frame
    // came from, because a "Non-Dresden Frame" line carries no shape or colour
    // at all and would otherwise show nothing under its name.
    frameLabel(item) {
      const sources = {
        existing_dresden: 'Existing Dresden Frame',
        non_dresden: 'Non-Dresden Frame',
      };

      return [
        sources[item.frame_source],
        item.glasses_type,
        item.frame_shape,
        item.frame_colour,
      ]
        .filter(Boolean)
        .join(' · ');
    },
    // Money with its currency spelled out. IOGO shows a bare "$" elsewhere,
    // which reads as AUD on USD/NZD/CAD orders — never do that here, where the
    // figures decide what goes on a customs form.
    formatMoney(value, currency) {
      const code = String(currency || 'AUD').toUpperCase();
      const symbols = { AUD: 'A$', USD: 'US$', NZD: 'NZ$', CAD: 'CA$' };
      const amount = Number(value || 0).toFixed(2);

      return `${symbols[code] || `${code} `}${amount}`;
    },
    // ISO-2 country code -> a flag emoji (regional indicator symbols).
    flagEmoji(code) {
      if (!/^[A-Za-z]{2}$/.test(code || '')) return '🏳️';
      const base = 0x1f1e6;
      return String.fromCodePoint(
        ...code
          .toUpperCase()
          .split('')
          .map(ch => base + ch.charCodeAt(0) - 65),
      );
    },
    countryMeta(code) {
      const c = (code || '').toUpperCase();
      const names = {
        AU: 'Australia', NZ: 'New Zealand', US: 'United States', PR: 'Puerto Rico',
        GB: 'United Kingdom', IE: 'Ireland', DE: 'Germany', FR: 'France', NL: 'Netherlands',
        BE: 'Belgium', NO: 'Norway', SE: 'Sweden', DK: 'Denmark', FI: 'Finland', CH: 'Switzerland',
        AT: 'Austria', IT: 'Italy', ES: 'Spain', PT: 'Portugal', PL: 'Poland', GR: 'Greece',
        CN: 'China', SG: 'Singapore', HK: 'Hong Kong', JP: 'Japan', KR: 'South Korea',
        TW: 'Taiwan', MY: 'Malaysia', TH: 'Thailand', ID: 'Indonesia', PH: 'Philippines',
        IN: 'India', AE: 'United Arab Emirates', ZA: 'South Africa', CA: 'Canada',
        MX: 'Mexico', BR: 'Brazil',
      };
      return { code: c, name: names[c] || c || '—', flag: this.flagEmoji(c) };
    },
    async parcelOnChange() {
      const parcel = await this.parcelSizes.find(
        parcelSize => parcelSize.value === this._.get(this.form, 'parcel_size_id'),
      );
      this.form.weight.units = parcel.units;

      // DP-4297: with an item list the weight comes from the items + this
      // box's tare, not from the box's old flat default.
      if (this.showItemsSection) {
        this.applyCalculatedWeight();
        return;
      }

      this.form.weight.value = parcel.weight;
    },
    showAddressModal() {
      this.$bvModal.show('addresses-picker-modal');
    },
    editAddress() {
      this.showAddAddressModal({
        ...this.selectedAddress,
        customer: this.customer,
      });
    },
    // DP-4609: block quotes and labels when the sender outlet's region cannot
    // ship the order's region. Skipped when either region is unknown.
    validateShippingRegion() {
      if (!this.orderRegionCode || !this.senderRegionCode) return true;
      const allowed = allowedSenderRegions[this.orderRegionCode] || [this.orderRegionCode];
      if (allowed.includes(this.senderRegionCode)) return true;
      const article = allowed[0] === 'CA' ? 'a' : 'an';
      const message =
        `${this.orderRegionCode} orders cannot be shipped from the ` +
        `${this.senderRegionCode} outlet. Please switch to ${article} ` +
        `${allowed.join(' or ')} outlet to create the shipping order.`;
      this.$store.dispatch('showErrorAlert', message);
      return false;
    },
    async getQuote() {
      this.couriers = [];
      this.quoteWarnings = {};
      const address = this._.get(this.toBeSubmittedAddress, 'address');
      const contact = this._.get(this.toBeSubmittedAddress, 'contact');

      if (!this.$refs.createShippingOrderForm.checkValidity()) {
        // Try focus on the error
        this.$refs.createShippingOrderForm.reportValidity();
        return;
      }

      if (!this.validateShippingRegion()) {
        return;
      }

      // DP-4137: block when the order has no shippable items or a ticked item
      // has no HS code.
      if (this.showItemsSection && this.customsItems.length === 0) {
        this.$store.dispatch(
          'showErrorAlert',
          'This order has nothing to ship — every line on it is a service (postage, a consultation, '
            + 'a warranty fee) rather than goods, so there is no parcel to label.',
        );
        return;
      }
      // DP-4478: "at least one item must be selected before Get Quotes".
      if (this.showItemsSection && this.selectedItems.length === 0) {
        this.$store.dispatch('showErrorAlert', 'Select at least one item to ship.');
        return;
      }
      if (this.showCustomsColumns && this.hasMissingHs) {
        this.$store.dispatch(
          'showErrorAlert',
          // DP-4612 round 2: this fires for a missing WEIGHT as well as a missing
          // tariff code (has_missing_hs covers both), and it never said where to
          // go. Matches the wording the backend uses when it blocks the label.
          'Australia Post needs a tariff code and a weight for every item on an international parcel, '
            + 'and at least one item here is missing one of them. Add the missing details under '
            + 'Shipping Customs \u2192 Customs by Product in Nova (or ask someone who can), then try again.',
        );
        return;
      }
      // DP-4672: a US quote from the wrong outlet prices an AusPost account
      // Zonos has never heard of, so it is refused before AusPost is called at
      // all — at Get Quotes rather than at Confirm.
      if (this.requiresZonosOutletSwitch) {
        this.$store.dispatch('showErrorAlert', this.zonosOutletMessage);
        return;
      }
      // DP-4672: and refused when we have no signed-off 10-digit US code for
      // something in the parcel. AusPost would reject the whole shipment at
      // Confirm with "HS Tariff Code for destination country US must be 10
      // numeric characters" — which is what every US label did in August.
      if (this.hasMissingUsHs) {
        this.$store.dispatch(
          'showErrorAlert',
          'Australia Post needs a 10-digit US tariff code for: '
            + `${this.missingUsHsItems.join(', ')}. We do not hold one for these yet — ask `
            + 'fulfilment to classify them before shipping this order to the US.',
        );
        return;
      }
      // DP-4466: over the EDN threshold the AusPost API must not be called.
      if (this.exceedsEdnThreshold) {
        this.$store.dispatch(
          'showErrorAlert',
          `This shipment value is over A$${this.ednThresholdLabel}. Australia Post requires an Export Declaration Number for international shipments over A$${this.ednThresholdLabel}. Please process this shipment directly in the Australia Post portal.`,
        );
        return;
      }
      // DP-4325: the weight is editable, so an invalid override must not reach
      // the quote or the label.
      if (!this.weightIsValid) {
        this.$store.dispatch(
          'showErrorAlert',
          'Enter a total shipment weight greater than 0 (for example 0.130) before getting quotes.',
        );
        return;
      }

      this.isLoading = true;
      const sendleQuoteParameters = {
        sender_outlet_id: this._.get(this.form, 'sender.outlet.id'),
        delivery_suburb: this._.get(address, 'suburb'),
        delivery_postcode: this._.get(address, 'postcode'),
        delivery_country: this._.get(address, 'country'),
        weight_value: this._.get(this.form, 'weight.value'),
        weight_units: this._.get(this.form, 'weight.units'),
        volume_value: this._.get(this.form, 'volume.value'),
        volume_units: this._.get(this.form, 'volume.units'),
        first_mile_option: 'pickup',
      };

      const parcel = await this.parcelSizes.find(
        parcelSize => parcelSize.value === this._.get(this.form, 'parcel_size_id'),
      );

      const auPostQuoteParameters = {
        sender_outlet_id: this._.get(this.form, 'sender.outlet.id'),
        country: this._.get(address, 'country'),
        delivery_postcode: this._.get(address, 'postcode'),
        weight: this._.get(this.form, 'weight.value'),
        width: parcel.width,
        height: parcel.height,
        length: parcel.length,
        quantity: this._.get(this.form, 'parcel_quantity'),
        receiver: {
          name: this._.get(contact, 'name'),
          lines: [this._.get(address, 'address1'), this._.get(address, 'address2')],
          suburb: this._.get(address, 'suburb'),
          state: this._.get(address, 'state'),
          country: this._.get(address, 'country'),
          postcode: this._.get(address, 'postcode'),
          phone: this._.get(contact, 'phone'),
          email: this._.get(contact, 'email'),
        },
      };

      try {
        if (this.isAddressCa && this.form.ca_order_tracking) {
          await apiOrders.getQuoteManuelEntry({
            tracking_id: this.form.ca_order_tracking,
            sender_outlet_id: this._.get(this.form, 'sender.outlet.id'),
            cargo_company: 'canada manual entry',
            order_id: this._.get(this.selectedOrder, 'id'),
          });

          this.$store.dispatch(
            'showSuccessAlert',
            'New shipping order has been successfully created!',
          );
          this.$bvModal.hide('create-shipping-order-modal');
          this.$root.$emit('reloadExpandedJob', this.selectedOrder.id);
          // DP-4478: the order's Shipping box reloads its counts.
          this.$root.$emit('shipmentsChanged', this.selectedOrder.id);
        } else {
          const response = await apiOrders.getQuote({
            aupost: auPostQuoteParameters,
            sendle: sendleQuoteParameters,
          });
          this.couriers = response.data.data;
        }
      } catch (error) {
        const data = error?.response?.data;
        if (Array.isArray(data?.errors?.errors)) {
          this.quoteWarnings = data.errors.errors.map(err => `${err.message}`);
        } else if (data?.errors) {
          // DP-4612 round 2: this used to print the field path in front of every
          // line ("receiver.state: The receiver.state field is required."). The
          // message says what is wrong on its own; the path never helped anyone
          // reading it.
          this.quoteWarnings = Object.values(data.errors).flat();
        } else {
          this.quoteWarnings = [
            data?.message || 'Unable to get shipping quotes. Please try again.',
          ];
        }
      } finally {
        this.courier = null;
        this.step = 2;
        this.isLoading = false;
      }
    },
    createOrder() {
      // DP-4478: the server no longer refuses a second label for an order, so
      // a second click while the first label is being created must not send
      // another one (the API also locks the order while a label is created).
      if (this.isLoading) return;
      if (!this.validateShippingRegion()) return;
      const selectedCourier = this.couriers.find(courier => courier.product_id === this.courier);
      if (selectedCourier) {
        if (selectedCourier.product_type === 'sendle') {
          this.createOrderBySendle();
        }
        if (selectedCourier.product_type === 'aupost') {
          this.createOrderByAuPost();
        }
      }
    },
    createOrderByAuPost() {
      const selectedCourier = this.couriers.find(courier => courier.product_id === this.courier);
      const parcel = this.parcelSizes.find(
        parcelSize => parcelSize.value === this._.get(this.form, 'parcel_size_id'),
      );
      const address = this._.get(this.toBeSubmittedAddress, 'address');
      const contact = this._.get(this.toBeSubmittedAddress, 'contact');
      const productId = selectedCourier.product_id;
      const orderId = this._.get(this.selectedOrder, 'id');
      const auPostForm = {
        selected_outlet_id: this.currentUser.outlet.id,
        sender_outlet_id: this._.get(this.form, 'sender.outlet.id'),
        product_id: productId,
        order_id: orderId,
        weight: this._.get(this.form, 'weight.value'),
        width: parcel.width,
        height: parcel.height,
        length: parcel.length,
        quantity: this._.get(this.form, 'parcel_quantity'),
        // DP-4478: the items ticked for this label; the API records them against
        // it. Reverses DP-4471, which stopped sending a selection because the
        // unticked items then had no way to ship.
        ...(this.showItemsSection ? { selected_item_ids: this.selectedItemIds } : {}),
        // DP-4305: the AUD values shown above, plus the rate they came from.
        // Omitted when no live rate was available — the backend then converts
        // with its configured fallback rate.
        ...this.customsValuePayload(),
        receiver: {
          name: this._.get(contact, 'name'),
          lines: [this._.get(address, 'address1'), this._.get(address, 'address2')],
          suburb: this._.get(address, 'suburb'),
          state: this._.get(address, 'state'),
          country: this._.get(address, 'country'),
          postcode: this._.get(address, 'postcode'),
          phone: this._.get(contact, 'phone'),
          email: this._.get(contact, 'email'),
        },
      };
      this.isLoading = true;
      apiOrders
        .createAuPostOrder(auPostForm)
        .then(() => {
          this.$store.dispatch(
            'showSuccessAlert',
            'New shipping order has been successfully created!',
          );
          this.$bvModal.hide('create-shipping-order-modal');
          this.$root.$emit('reloadExpandedJob', this.selectedOrder.id);
          // DP-4478: the order's Shipping box reloads its counts.
          this.$root.$emit('shipmentsChanged', this.selectedOrder.id);
        })
        .catch(error => {
          // Whatever went wrong this time, last attempt's list is no longer true.
          this.errors = [];
          if (Array.isArray(error.response?.data)) {
            const [auPostError] = error.response.data;
            if (
              typeof auPostError?.errors === 'string' &&
              typeof auPostError.message === 'string'
            ) {
              this.$store.dispatch(
                'showErrorAlert',
                `${auPostError.errors} (${auPostError.message})`,
              );
              return;
            }
          }
          // DP-4612 round 2: a refused label usually has more than one reason,
          // and the banner shows a single line that then auto-dismisses. So
          // every reason is also listed in this modal's own alert, which stays
          // until it is dismissed and sits next to the fields it is about.
          const data = error.response?.data ?? {};
          if (data.errors?.errors) {
            // Australia Post's own error shape: [{ message }].
            this.errors = data.errors.errors;
          } else if (data.errors && typeof data.errors === 'object') {
            // Laravel validation: { 'receiver.state': ['...'], ... }.
            this.errors = Object.values(data.errors)
              .flat()
              .map(message => ({ message }));
          }
          this.$store.dispatch(
            'showErrorAlert',
            data.message || 'Unable to create shipping label. Please try again.',
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    createOrderBySendle() {
      const address = this._.get(this.toBeSubmittedAddress, 'address');
      if (address) {
        this.form.receiver.address = {
          ...address,
          address_line1: this._.get(address, 'address1'),
          address_line2: this._.get(address, 'address2'),
          state_name: this._.get(address, 'state'),
        };
        this.form.receiver.contact = this.toBeSubmittedAddress.contact;
        this.form.contents.description = this.form.description;
      }

      this.form.sender.outlet_id = this._.get(this.form, 'sender.outlet.id');

      this.isLoading = true;
      const selectedCourier = this.couriers.find(courier => courier.product_id === this.courier);
      const toBeSubmittedForm = {
        ...this.form,
        pickup_date: selectedCourier.delivery_estimate[1],
        order_id: this.selectedOrder.id,
      };

      apiOrders
        .createSendleOrder(toBeSubmittedForm)
        .then(response => {
          this.$store.dispatch(
            'showSuccessAlert',
            'New shipping order has been successfully created!',
          );
          this.$bvModal.hide('create-shipping-order-modal');
          const orderId = this._.get(this.selectedOrder, 'id');
          const userId = this._.get(this.currentUser, 'id');
          const trackingUrl = this._.get(response, 'data.tracking_url');
          const sendleOrderId = this._.get(response, 'data.order_id');
          const note = `
            Tracking URL: ${trackingUrl}
            Label: https://app.sendle.com/dashboard/sent_orders/${sendleOrderId}/label?size=cropped
          `;
          apiOrders.postOrderNote({
            orderId,
            note,
            user_id: userId,
          });

          this.updateJob({
            ...this.selectedOrder,
            shipment_company: 'SENDLE',
            shipment_status: 'created',
            shipment_tracking_id: response.data.sendle_reference,
          });
          this.$root.$emit('reloadExpandedJob', this.selectedOrder.id);
        })
        .catch(error => {
          if (error.response?.data?.errors) {
            this.errors = Array.isArray(error.response?.data?.errors)
              ? Object.entries(error.response.data.errors).map(([key, value]) => ({
                  message: `${key}: ${value}`,
                }))
              : [{ message: error.response?.data?.errors }];
          }
          this.$store.dispatch('showErrorAlert', error.response.data.message);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<!-- DP-4137: international customs declaration UI -->
<style scoped>
.dp-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #718098;
}
.dp-loading {
  color: #718098;
}
/* Customs summary strip */
.dp-customs-summary {
  display: grid;
  grid-template-columns: 0.8fr 1fr 1fr 1.1fr;
  background: #f4f6f9;
  border: 1px solid #ebeef4;
  border-radius: 4px;
  overflow: hidden;
}
.dp-cs-cell {
  padding: 11px 15px;
  border-left: 1px solid #ebeef4;
}
.dp-cs-cell:first-child {
  border-left: 0;
}
.dp-cs-label {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8e939a;
  font-weight: 600;
  margin-bottom: 4px;
}
.dp-cs-value {
  font-size: 15px;
  font-weight: 600;
  color: #1b1f26;
  display: flex;
  align-items: center;
  gap: 7px;
}
.dp-cs-value small {
  font-size: 12px;
  font-weight: 500;
  color: #718098;
}
.dp-duty {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
}
.dp-duty--receiver {
  background: #e7f6ec;
  color: #2e9d52;
}
.dp-duty--prepaid {
  background: #fdf3e0;
  color: #a9761c;
}
.dp-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

/* DP-4513: declaration values — the new card design. Header and rows are
   separate grids sharing one column template so the boxes line up. */
.dp-decl-grid {
  display: grid;
  align-items: center;
  gap: 10px;
}
/* DP-4478: first column is the item's checkbox. */
.dp-decl-grid--intl {
  grid-template-columns: 22px minmax(0, 1.4fr) 128px minmax(120px, 0.9fr) 110px 96px;
}
.dp-decl-grid--domestic {
  grid-template-columns: 22px minmax(0, 1fr) 110px;
}
.dp-decl-head {
  padding: 0 14px;
  margin-bottom: 6px;
}
.dp-decl-head span {
  font-size: 12px;
  font-weight: 600;
  color: #1b1f26;
}
.dp-decl-row {
  background: #eaf2fd;
  /* DP-4478: same 1px border as an unticked row, so ticking one does not
     change its size or shift the value boxes. */
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 8px;
}
.dp-decl-row--missing {
  background: #fbe9e9;
}
/* DP-4478: an unticked item is not in this parcel — greyed out like Figma
   frame 1591; its checkbox stays live. */
.dp-decl-row--unselected {
  background: #fafafa;
  border: 1px solid #f4f4f5;
}
.dp-decl-row--unselected .dp-decl-name,
.dp-decl-row--unselected .dp-decl-box {
  opacity: 0.55;
}
.dp-decl-check {
  display: flex;
  align-items: center;
}
/* A native checkbox: io-go's global .custom-control styling is tuned for
   labelled rows and mis-seats an unlabelled one (see DP-4137 2b10596). */
.dp-check {
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: #2a68f5;
}
.dp-decl-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  min-width: 0;
  cursor: pointer;
}
/* DP-4478: the name keeps at least 160px beside the Dispatched badge; in a
   narrower popup (800px under 1200px screens) the badge drops below the name
   instead of squeezing it into a one-word column. */
.dp-decl-item .dp-decl-name {
  flex: 1 1 160px;
}
/* No margin-left:auto — the growing name already pushes it right on a shared
   line, and on its own line (narrow popup) it starts under the name. */
.dp-dispatched {
  flex: 0 0 auto;
  margin-right: 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 8px 10px;
  border-radius: 100px;
  background: #5ac072;
  color: #fff;
  white-space: nowrap;
}
.dp-select-hint {
  font-size: 12.5px;
  color: #d83e3e;
  font-weight: 500;
}
.dp-decl-name {
  min-width: 0;
}
/* The white value boxes under the column heads. */
.dp-decl-box {
  display: block;
  width: 100%;
  background: #fff;
  border: 1px solid #d9dfe8;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12.5px;
  line-height: 1.3;
  color: #1b1f26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dp-decl-box--hs {
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  font-weight: 600;
  color: #2c3a4d;
}
.dp-decl-box--missing {
  color: #d83e3e;
  border-color: #f0c2c2;
  font-weight: 600;
}
.dp-decl-box--wt {
  font-variant-numeric: tabular-nums;
}
.dp-item-name {
  font-weight: 500;
  color: #1b1f26;
}
.dp-qty {
  color: #8e939a;
  font-weight: 500;
  margin-left: 5px;
}
/* Paid-for lines that are not declarable goods. */
.dp-excluded {
  font-size: 12px;
  color: #8e939a;
}
/* DP-4612 round 2: an error or warning is written as several short lines — what
   is wrong, then what to do about it. Keep those line breaks. */
.dp-multiline {
  white-space: pre-line;
}
/* DP-4305: exchange-rate line above the items table. */
.dp-fx {
  font-size: 12.5px;
  color: #5b616b;
}
.dp-fx-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #17c964;
  margin-right: 6px;
  vertical-align: middle;
}
.dp-fx-dot--warn {
  background: #f5a524;
}
/* DP-4473: frame shape · colour, under the item name. */
.dp-item-frame {
  display: block;
  color: #5b616b;
  font-size: 12px;
  font-weight: 500;
  margin-top: 2px;
}
/* DP-4313: order item id, under the item name. */
.dp-item-id {
  display: block;
  color: #8e939a;
  font-size: 11.5px;
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  margin-top: 2px;
}
@media (max-width: 720px) {
  .dp-customs-summary {
    grid-template-columns: 1fr 1fr;
  }
  .dp-cs-cell {
    border-left: 0;
    border-top: 1px solid #ebeef4;
  }
  .dp-cs-cell:nth-child(-n + 2) {
    border-top: 0;
  }
  /* DP-4513: the five-column card grid can't fit a phone — stack the value
     boxes under the item instead. */
  .dp-decl-head {
    display: none;
  }
  .dp-decl-grid--intl,
  .dp-decl-grid--domestic {
    grid-template-columns: 1fr 1fr;
  }
  .dp-decl-item,
  .dp-decl-check {
    grid-column: 1 / -1;
  }
}
</style>
